/**
 * quotePrefill.js — Prefill logic for the Quote Builder.
 *
 * Implements rules P-1 through P-5:
 *   P-1: Customer → contact card (name, role, email, phone)
 *   P-2: Salesperson prefilled from customer's assigned salesperson
 *   P-3: Payment term + type prefilled from customer's default
 *   P-4: Billing address + Job/Service address with "same as billing" (default ON)
 *   P-5: Lead source — only shown when adding new customer inline
 *
 * Also handles:
 *   - Create-customer writeback (new customer created from builder)
 *   - Uploads → Documents association
 */

// PT-53A W1.4b (2026-07-29): line-level camelCase→snake_case field mapping
// extracted into a shared primitive at `lib/serializers/lineFieldMapper.js`.
// `serializeLineMargin` re-exported below for back-compat with legacy comments
// in existing tests.
import { serializeLineMargin, mapLineFields } from '../lib/serializers/lineFieldMapper';
import { normaliseSalespeople } from '../utils/normaliseSalespeople';
export { serializeLineMargin };

// IDI-41: migrated (tenant-270) quotes return line items EMBEDDED in
// `data.areas[].lineItems` (assembled by the backend's fetchQuoteRelated), and
// leave the flat top-level `data.line_items` EMPTY. The QuoteBuilder reads the
// flat array, so those quotes rendered "No line items yet" / $0.00 even though
// the data was present. This flattens the area-embedded items into the flat
// shape the builder groups on, converting the backend `*Cents` money fields to
// the builder's dollar fields and stamping each item with its area association.
//
// Backend line_item entity (per fetchQuoteRelated):
//   { id, name, itemId, quoteAreaId, sqft,
//     itemCostCents, materialCostCents, laborCostCents, totalCostCents }
// Builder flat shape (dollars + camelCase labels):
//   { id, productLabel, areaId, areaLabel, categoryId, quantity, sqft,
//     materialCost, laborCost, otherCost, line_total }
//
// IDI-47 (units): useQuoteMargins/marginEngine.computeLineItem work entirely in
// CENTS — the same unit the normal data.line_items path carries. This flatten
// path (migrated quotes whose data.line_items is empty) MUST also emit CENTS.
// Emitting dollars made the engine read $523.44 as 523 cents, collapsing the
// Bid Total ~140x ($6,285 rendered as $44.64).
//
// IDI-47 (field): the line labor cost is `totalLaborCostCents` (source
// Area_TotalLaborCost), NOT `laborCostCents` (the raw per-sqft RATE, e.g.
// $0.12). `materialCostCents`/`totalCostCents` are already true costs.
function toCents(c) {
  const n = Number(c);
  return Number.isFinite(n) ? Math.round(n) : 0;
}

// IDI-47 (margin decision, Option 1): when the quote carries a derived
// `overall_margin` (migration backfill or fresh save), the margin engine
// should be allowed to cascade a real margin onto these lines instead of
// reading a frozen 0%-margin "price == cost" stamp. `hasOverallMargin`
// gates that: when true, we omit line_total/lineTotal entirely so
// computeLineItem's isRealPriceOverride check never fires and the fresh
// cascade (line > category > customer > customerType > quote overall)
// takes over, pricing up to the quote's actual bid margin.
//
// When false (no derivable overall_margin — e.g. cost exceeded the source
// bid, or there was no bid at all), we keep stamping line_total = cost so
// the line renders at 0% rather than silently inheriting the tenant's
// unrelated default margin (see decision doc: "do NOT apply the tenant
// default 35% to migrated quotes").
export function flattenAreaLineItems(areas, hasOverallMargin = false) {
  if (!Array.isArray(areas)) return [];
  const out = [];
  for (const area of areas) {
    const areaId = area.areaId || area.id || 'uncategorized';
    const areaLabel = area.name || area.areaLabel || area.area_label || '';
    const items = Array.isArray(area.lineItems)
      ? area.lineItems
      : (Array.isArray(area.line_items) ? area.line_items : []);
    for (const li of items) {
      // CENTS — matches data.line_items and the margin engine's contract.
      const materialCost = toCents(li.materialCostCents);
      // IDI-47: line labor cost = totalLaborCostCents (not the per-sqft rate).
      const laborCost = li.totalLaborCostCents != null
        ? toCents(li.totalLaborCostCents)
        : toCents(li.laborCostCents);
      // Prefer the explicit assembled total; else sum the (total) components.
      const costTotal = li.totalCostCents != null
        ? toCents(li.totalCostCents)
        : materialCost + laborCost + toCents(li.itemCostCents);
      out.push({
        id: li.id,
        itemId: li.itemId,
        productLabel: li.name || li.productLabel || li.product_label || '',
        areaId,
        areaLabel,
        // UI-trace verification (2026-09-04): CommittedLineCard's title falls
        // back to productLabel when subAreaLabel is absent -- confirmed live
        // this was ALWAYS absent from this function's output (never read from
        // the source item at all), even though the raw migrated item DOES
        // carry it (li.subArea, the resolved sub-area name; stamped onto every
        // migrated line by the backend's sub-area grouping). Every migrated
        // line's card title was silently showing the product name instead of
        // its sub-area ("Sound Ceilings", "Rim Joists - Batt", etc.) with no
        // error or fallback-to-"Untitled" to signal the gap -- confirmed on
        // quote 12353's real data.
        subAreaLabel: li.subAreaLabel || li.subArea || li.sub_area_label || '',
        categoryId: li.categoryId || li.category_id || null,
        quantity: li.quantity != null ? Number(li.quantity) : (li.sqft != null ? Number(li.sqft) : null),
        sqft: li.sqft != null ? Number(li.sqft) : null,
        materialCost,
        laborCost,
        otherCost: 0,
        // IDI-47 (2026-09-04 follow-up): project the per-LINE margin
        // (Area_GrossMargin -> migrated `grossMargin`, percent-scale e.g. 40)
        // into the engine's `lineMargin` (decimal, e.g. 0.40) when the source
        // line genuinely carried one. This was Option A from the 2026-08-19
        // note on this exact gap ("fast, can ship now") -- confirmed live
        // that migrated lines DO carry a real per-line grossMargin (e.g.
        // quote 12353's first two lines, both 40%, differing from that
        // quote's OWN quote-level 45% margin) and it was being silently
        // dropped here, forcing every migrated line down to the quote-level
        // cascade tier even when the source had a more specific answer.
        // normalizeMarginValue handles the percent/decimal ambiguity and
        // rejects >=100% (a migrated garbage value) rather than propagating it.
        lineMargin: normalizeMarginValue(li.grossMargin ?? li.gross_margin),
        // IDI-47: only stamp a price override when we have nowhere better
        // for the margin to come from. See gate comment above.
        ...(hasOverallMargin ? {} : { line_total: costTotal, lineTotal: costTotal }),
        quoteAreaId: li.quoteAreaId,
        _source: 'idi41_area_flatten',
      });
    }
  }
  return out;
}

// resolveLoadedLineItems (IDI-310, 2026-09-10) — the load-time line-item
// precedence rule, extracted into a pure function so it can be falsified
// directly rather than only indirectly through a mounted <QuoteBuilderPage>.
//
// The backend has TWO distinct producers of `quote.line_items`, and they
// emit DIFFERENT shapes:
//   1. projectFlatLineItems (cmd/quotes/main.go) — runs on EVERY read of a
//      migrated quote that has no genuinely stored flat array, and
//      re-derives `line_items` fresh from areas[] every time. Its items
//      carry the RAW entity fields (materialCostCents, laborCostCents,
//      itemCostCents, totalCostCents) — the shape flattenAreaLineItems()
//      exists to convert. This is NOT a builder save.
//   2. A genuine builder save (assembleQuotePayload → serializeLineItem →
//      mapLineFields) — its items carry the MAPPED fields (item_name,
//      category, cost_basis, margin_pct/margin_override, unit_rate,
//      line_total). This IS a real write and must win outright — discarding
//      it is exactly the data loss IDI-310 reported: a previous version of
//      this logic preferred `areas[]` unconditionally, so a freshly-saved
//      line (correctly persisted in `line_items`) was silently discarded on
//      the next load because the stale migrated `areas[]` still existed.
//
// Discriminator: mapLineFields ALWAYS emits `cost_basis` (see
// lib/serializers/lineFieldMapper.js:119, unconditional) — a
// backend-projected migrated line never has that key. Using this instead of
// raw non-emptiness lets a builder save always win (fixing the data loss)
// while a never-edited migrated quote still gets the flattenAreaLineItems()
// treatment (keeping the IDI-47 margin-engine fix intact).
export function resolveLoadedLineItems(data) {
  const flatCandidate = Array.isArray(data?.line_items) && data.line_items.length > 0
    ? data.line_items
    : (Array.isArray(data?.lineItems) && data.lineItems.length > 0 ? data.lineItems : null);
  const isBuilderSaved = Array.isArray(flatCandidate)
    && flatCandidate.some((li) => li && Object.prototype.hasOwnProperty.call(li, 'cost_basis'));
  if (isBuilderSaved) {
    return flatCandidate;
  }
  if (Array.isArray(data?.areas) && data.areas.length > 0) {
    const quoteHasOverallMargin = normalizeMarginValue(
      data.overall_margin ?? data.overallMargin ?? data.grossMargin
    ) != null;
    return flattenAreaLineItems(data.areas, quoteHasOverallMargin);
  }
  // Neither shape signal fired (e.g. a pre-PR-02b flat-only test quote with
  // no areas[] at all) — fall back to the raw array rather than showing
  // nothing.
  return flatCandidate || [];
}

// ─── P-1: Customer → Contact Prefill ────────────────────────────────────────

// PT-30 (2026-07-26): normalize a `default_margin` value coming off a customer
// record. Customer forms accept 0–100 (percentage); some legacy records may
// already be stored as a decimal 0..1. Return `null` if unset/invalid so the
// engine's cascade proceeds to the next Lindsey level.
//
// ⚠️ Do NOT use for `grossMargin` — that field is percent-scale by definition
// (287_ §2.1, 288_ §2). The `n > 1 ? n / 100 : n` heuristic below would misread
// a genuine sub-1% margin (e.g. quote 17136 grossMargin "-6.3012301293").
// grossMargin normalization is `gmNum / 100`, unconditional — see QuoteBuilderPage.
export function normalizeMarginValue(raw) {
  if (raw == null || raw === '') return null;
  const n = typeof raw === 'string' ? parseFloat(raw) : raw;
  if (!Number.isFinite(n) || n < 0) return null;
  // If someone stored 68 (from an input labeled %), coerce to 0.68.
  // If it's already a decimal <= 1, pass through.
  // §2a (2026-08-08): reject values that normalise to >= 1.0 (margin >= 100%
  // means sell below cost → negative price). Log and fall through to the next tier.
  const decimal = n > 1 ? n / 100 : n;
  if (decimal >= 1) {
    // eslint-disable-next-line no-console
    console.warn(`[normalizeMarginValue] rejected: raw=${JSON.stringify(raw)} \u2192 ${decimal} (>= 1.0). Falling through.`);
    return null;
  }
  return decimal;
}

/**
 * Project a customer's server contact rows into the builder's `contact` +
 * `allContacts` prefill shape, carrying each row's real id.
 *
 * IDI-341: shared by prefillFromCustomer (load) and the net-new-customer
 * create path in QuoteBuilderPage, so both adopt the SERVER ids. A contact
 * without its server id is re-created on the next writeback.
 */
export function projectCustomerContacts(contacts = []) {
  const list = Array.isArray(contacts) ? contacts : [];
  const primaryContact = list.find((c) => c.isPrimary || c.is_primary) || list[0] || null;
  const nameOf = (c) => c.name || `${c.first_name || c.firstName || ''} ${c.last_name || c.lastName || ''}`.trim();
  return {
    contact: primaryContact ? {
      id: primaryContact.id,
      name: nameOf(primaryContact),
      role: primaryContact.role || primaryContact.title || '',
      email: primaryContact.email || '',
      phone: primaryContact.phone || primaryContact.mobile || '',
    } : null,
    allContacts: list.map((c) => ({
      id: c.id,
      name: nameOf(c),
      role: c.role || c.title || '',
      email: c.email || '',
      phone: c.phone || c.mobile || '',
      isPrimary: c.isPrimary || c.is_primary || false,
    })),
  };
}

/**
 * P-1: Extract contact card data from a customer record.
 *
 * @param {object} customer - Customer data from API
 * @param {object} [opts]
 * @param {string} [opts.defaultPaymentTerm] - IDI-652: the dealer's Quote
 *   Defaults term, used when the customer has none (before 'net_30').
 * @returns {object} Prefilled contact data
 */
export function prefillFromCustomer(customer, opts = {}) {
  if (!customer) return emptyPrefill();

  // Primary contact
  const contacts = customer.contacts || [];

  return {
    // Customer info
    customerId: customer.id,
    customerName: customer.name || customer.company_name || '',

    // PT-30 · Lindsey level 2 (Customer Specific Margin). Persisted as
    // `customer.default_margin` (0–100 on the form; normalized to 0..1 decimal here).
    // Consumers hand this to `useQuoteMargins`’ `customerMargin` option.
    customerDefaultMargin: normalizeMarginValue(customer.default_margin),

    // PT-30 · Upstream input for Lindsey level 3. The customer's *type* (e.g.
    // 'residential', 'commercial'); consumers look up this type's default margin
    // in Settings→Customer Types and pass it as `customerTypeMargin`.
    customerType: customer.customer_type || customer.type || null,

    ...projectCustomerContacts(contacts),

    // P-2: Salesperson (revised 2026-07-31 after 1a8760d didn't fix the issue)
    //
    // Wire shape (traced 2026-07-31): the customer endpoint returns
    // `salespersons: string[]` — an array of display-name strings like
    // ["Jane Doe", "John Smith"]. This comes from AddCustomerDrawer /
    // CustomerFormPanel writing `form.salesperson` (a string[] from
    // TypeaheadChips) verbatim. The backend is field-agnostic.
    //
    // The previous fix assumed [{id, name}] objects. When given strings,
    // sp.id and sp.name were undefined — producing [{id: '', name: ''}]
    // and no visible prefill. This normaliser accepts BOTH shapes:
    //  - string:       'Jane Doe'          → { id: 'Jane Doe', name: 'Jane Doe' }
    //  - object:       { id, name }        → preserved
    //  - legacy:       { salesperson_id }  → mapped
    //
    // Downstream: prefill.salespersons feeds the SalespersonPicker via
    // salesperson_ids: prefill.salespersons.map(s => s.id) in
    // assembleQuotePayload. Using the name as the id preserves the string
    // identity used everywhere else in the app until the contract mapper
    salespersons: normaliseSalespeople(customer),

    // P-3: Payment terms
    // IDI-495: the customer record stores `payment_terms` (plural; see
    // CustomerFormPanel buildPayload). Reading only the singular meant a new
    // quote never carried the customer's terms and always seeded net_30.
    paymentTerm: customer.payment_terms || customer.payment_term || customer.paymentTerms || customer.paymentTerm
      || customer.default_payment_term || opts.defaultPaymentTerm || 'net_30',
    paymentType: customer.payment_type || customer.paymentType || customer.default_payment_type || '',

    // P-4: Addresses
    billingAddress: extractAddress(customer, 'billing'),
    serviceAddress: extractAddress(customer, 'service'),
    sameAsBilling: true, // P-4: Default ON

    // P-5: Lead source — quote-scoped, NOT customer-scoped.
    // Initialisation defaults live in emptyPrefill(); prefillFromCustomer()
    // must NOT emit these or any spread over quote data clobbers them.
    isNewCustomer: false,
  };
}

/**
 * Create an empty prefill object (for new quotes without a customer).
 */
export function emptyPrefill() {
  return {
    customerId: null,
    customerName: '',
    customerDefaultMargin: null, // PT-30 · Lindsey level 2
    customerType: null,          // PT-30 · upstream input for Lindsey level 3
    contact: null,
    allContacts: [],
    salespersons: [],
    paymentTerm: 'net_30',
    paymentType: '',
    billingAddress: emptyAddress(),
    serviceAddress: emptyAddress(),
    sameAsBilling: true, // P-4: Default ON
    isNewCustomer: false,
    leadSource: '',
    // IDI-169: Project details
    jobTypeId: '',
    measurementMethod: '',
    // IDI-178: legacy per-quote toggle, defaults OFF. See
    // ProjectDetailsCard.js for the full rationale.
    roundMaterials: false,
  };
}

/**
 * P-5: Create prefill for a new customer being created inline.
 * Lead source field is ONLY shown in this case.
 *
 * @param {object} newCustomerData - Partial customer data from inline creation
 * @returns {object} Prefill with isNewCustomer=true and leadSource visible
 */
export function prefillNewCustomer(newCustomerData = {}) {
  return {
    customerId: null,
    customerName: newCustomerData.name || '',
    customerDefaultMargin: null, // PT-30 · new customer, not yet persisted
    customerType: newCustomerData.customer_type || null,
    contact: newCustomerData.contact || null,
    allContacts: newCustomerData.contact ? [newCustomerData.contact] : [],
    salespersons: [],
    paymentTerm: 'net_30',
    paymentType: '',
    billingAddress: newCustomerData.address || emptyAddress(),
    serviceAddress: newCustomerData.address || emptyAddress(),
    sameAsBilling: true,
    isNewCustomer: true,  // P-5: enables lead source field
    leadSource: newCustomerData.leadSource || '',
  };
}

// ─── P-4: Address Handling ───────────────────────────────────────────────────

/**
 * Extract an address from a customer record.
 *
 * @param {object} customer - Customer data
 * @param {string} type - 'billing' or 'service'
 * @returns {object} Address object
 */
function extractAddress(customer, type) {
  if (!customer) return emptyAddress();

  // Try typed address first
  const prefix = type === 'billing' ? 'billing_' : 'service_';
  const typed = {
    street1: customer[`${prefix}street1`] || customer[`${prefix}address_line1`] || '',
    street2: customer[`${prefix}street2`] || customer[`${prefix}address_line2`] || '',
    city: customer[`${prefix}city`] || '',
    state: customer[`${prefix}state`] || '',
    zip: customer[`${prefix}zip`] || customer[`${prefix}postal_code`] || '',
    country: customer[`${prefix}country`] || 'US',
  };

  // If typed address has data, use it
  if (typed.street1 || typed.city) return typed;

  // Fall back to generic address fields
  // §3 (293_): also read singular `address`/`serviceAddress` — the wire
  // carries these on 12/12 customers where `addresses` is empty on all 12.
  let addresses = customer.addresses || [];
  if (addresses.length === 0) {
    const a = customer.address;
    if (a && (a.street || a.city || a.zip)) {
      addresses = [{ street1: a.street, city: a.city, state: a.state, zip: a.zip, is_primary: true }];
    } else {
      const s = customer.serviceAddress;
      if (s && (s.address1 || s.city || s.postal)) {
        addresses = [{ street1: s.address1, city: s.city, state: s.state, zip: s.postal, is_primary: true }];
      }
    }
  }
  const match = addresses.find((a) => a.type === type) || addresses[0];
  if (match) {
    return {
      street1: match.street1 || match.address_line1 || match.line1 || '',
      street2: match.street2 || match.address_line2 || match.line2 || '',
      city: match.city || '',
      state: match.state || '',
      zip: match.zip || match.postal_code || '',
      country: match.country || 'US',
    };
  }

  // Fall back to flat address fields
  return {
    street1: customer.address || customer.street || '',
    street2: customer.address2 || '',
    city: customer.city || '',
    state: customer.state || '',
    zip: customer.zip || customer.postal_code || '',
    country: customer.country || 'US',
  };
}

/**
 * Create an empty address object.
 */
export function emptyAddress() {
  return {
    street1: '',
    street2: '',
    city: '',
    state: '',
    zip: '',
    country: 'US',
  };
}

/**
 * Q-09 (2026-08-18, §3ap): "has a value" for an address means "has a STREET,"
 * not `!= null`. `emptyAddress()` returns a populated object of empty
 * strings; a truthiness check on that object passes and can wipe a real
 * address on the server. This predicate guards the quote payload's
 * addresses (buildQuoteData). The other write path, buildCustomerWriteback,
 * was removed by IDI-425: the quote no longer writes the customer.
 *
 * Accepts both wire shapes seen historically: `street` (canonical singular)
 * and `street1` (this module's typed shape).
 */
export function hasStreet(addr) {
  if (!addr || typeof addr !== 'object') return false;
  const street = addr.street || addr.street1 || addr.line1 || addr.address1;
  return typeof street === 'string' && street.trim().length > 0;
}

// ─── P-4: Same-as-Billing Toggle ────────────────────────────────────────────

/**
 * P-4: Apply "same as billing" logic.
 * When toggle is ON, service address mirrors billing address.
 *
 * @param {object} quoteData - Current quote state
 * @param {boolean} sameAsBilling - Toggle state
 * @returns {object} Updated quote data with synced addresses
 */
export function applySameAsBilling(quoteData, sameAsBilling) {
  if (!sameAsBilling) return quoteData;

  return {
    ...quoteData,
    serviceAddress: { ...quoteData.billingAddress },
    sameAsBilling: true,
  };
}

// ─── Quote Data Assembly ─────────────────────────────────────────────────────

/**
 * Assemble the full quote data object from prefill + line items.
 * This is what gets sent to the auto-save endpoint.
 *
 * @param {object} prefill - Prefill state
 * @param {Array} lineItems - Line items array
 * @param {object} metadata - Additional quote metadata (notes, discounts, quoteOptions, etc.)
 * @returns {object} Complete quote payload for API
 *
 * PR-4 §1b: `quote_options` and `discounts` are now shipped as first-class
 * fields. Options are created via the standard AddItemCascade flow with
 * areaId:'options' and live in `line_items[]` with `areaLabel === 'Options'`,
 * so `quote_options` may be empty. The field ships anyway so the payload is
 * future-safe and the reader (QuoteReviewSend) can consume from either source
 * via `separateOptions`. Backend may silently drop these fields today; that's
 * tracked as tech debt (see PR-4 body).
 */
// PT-31 (2026-07-27): map the frontend's single nullable `lineMargin` field
// to the backend's two-field contract (`margin_pct` + `margin_override`).
// The two-field shape is deliberately preserved on the server because it
// distinguishes an explicit 0 (override:true, pct:0) from "not set"
// (override:false, pct:0) — PT-30's zero-is-valid rule requires that
// disambiguation. Prior to this map the frontend shipped `lineMargin`
// verbatim; `json.Unmarshal` dropped it silently, so per-line overrides did
// not survive save→reload. See PT-31 report for the full trace.
//
// Reverse mapping (read side) lives in `normalizeIncomingLineItem` below;
// hydration accepts BOTH shapes so quotes saved before this change (which
// have neither field) don't lose their inherited-margin behaviour.
//
// PT-53A W1.4b (2026-07-29): field mapping extracted to shared primitive
// (see import at top of file).

// PT-53 §53.3a (2026-07-30): remap builder-native line-item keys to the
// display contract read by QuoteDetail / RecordDecisionPage / QuotesPage.
// Prior to this change `serializeLineItem` was a pass-through spread; every
// downstream reader was expected to know the builder's camelCase field names
// (productLabel, categoryId, lineTotal, materialCost/laborCost/otherCost).
// PT-31 fixed `margin_pct` on this same serializer and left the sibling fields
// behind — D-041/D-043/D-044/D-045/D-046 are the surfaced consequences.
//
// Meta-check applied per proposed v5.1 standing rule: every field crossing
// this boundary is audited here, in one place. New readers should consume the
// snake_case display contract; do NOT scatter fallback branches (`item.foo ||
// item.fooBar || item.foo_bar`) across call sites.
//
// The builder-native keys are preserved via `...line` so a load→save→reload
// round-trip keeps the builder's state intact (see risk 1 in PT-53 plan).
//
// `computed` is the paired entry from `useQuoteMargins`'s `computedLines[i]`
// — its `total` is the authoritative price-per-line for normally-computed
// rows. `line.lineTotal` is only set when the user overrode price manually.
function serializeLineItem(line, computed = null) {
  if (!line || typeof line !== 'object') return line;
  // PT-53A W1.4b: line-level camelCase→snake_case mapping delegated to the
  // shared `mapLineFields` primitive. Builder-native keys are preserved via
  // the `...line` spread so save→reload round-trips.
  const mapped = mapLineFields(line, computed);
  return {
    ...line,
    ...mapped,
  };
}

/**
 * IDI-313 (2026-09-10): activation gate for the "first save with line items"
 * rule. A brand-new quote is created as `draft` (cmd/quotes/main.go:1107);
 * nothing ever flips it to `active` unless the user explicitly sends it or
 * marks it unsent. Convert to Job requires active/sent/won — a draft quote
 * with real line items could never become a job. This function is the
 * single source of truth for "should this save activate the quote", used
 * by QuoteBuilderPage's payload memo so the condition is unit-testable
 * without rendering the full builder.
 *
 * @param {string|null} currentStatus - the quote's in-memory status
 * @param {number} lineItemsCount - lineItems.length
 * @returns {boolean}
 */
export function shouldActivateOnSave(currentStatus, lineItemsCount) {
  return currentStatus === 'draft' && lineItemsCount > 0;
}

export function assembleQuotePayload(prefill, lineItems, metadata = {}, computedLines = []) {
  // IDI-305: internalNotes is destructured only so a stray value never spreads
  // into the payload. Internal notes are written by ?action=add_note, never
  // by autosave.
  // eslint-disable-next-line no-unused-vars
  const { title, discounts, quoteOptions, notes, internalNotes, validityDays, ...restMetadata } = metadata;
  // DEF-151: extract private snapshot-source fields before they spread into the API payload.
  const { _loadedSubtotal, _loadedSubtotalSnapshot, ...rest } = restMetadata;
  // IDI-351: never send a blank quote number. The number is server-assigned;
  // a migrated quote loads with none, and spreading `quoteNumber: null` into
  // every PUT used to clear it server-side (the server now ignores a blank
  // one too). A real number is still echoed.
  const cleanRestMetadata = { ...rest };
  for (const k of ['quoteNumber', 'quote_number']) {
    if (k in cleanRestMetadata && (cleanRestMetadata[k] == null || String(cleanRestMetadata[k]).trim() === '')) {
      delete cleanRestMetadata[k];
    }
  }
  const computedArr = Array.isArray(computedLines) ? computedLines : [];
  const serializedLineItems = Array.isArray(lineItems)
    ? lineItems.map((line, i) => serializeLineItem(line, computedArr[i] || null))
    : [];
  return {
    // PT-16 (2026-07-24): guard against serializing `customer_id: null`. The
    // backend's PUT handler treats "field absent" as "leave column unchanged"
    // but treats `field = null` as an explicit clear. Autosave must never
    // silently clear the saved customer, even if an unexpected upstream write
    // path (regression, race, or future refactor) puts `prefill.customerId`
    // into a falsy state. PT-15 #1 fixed the loader; this fixes the writer.
    //
    // FL-02 (2026-07-31): also emit `customerId` (camelCase). The Go
    // backend's createQuote handler validates on `body["customerId"]`
    // (main.go:377) while historically the payload used snake_case only.
    // Both forms are written so the create validation passes; the update
    // path accepts either. The backend team should collapse to one on
    // their side; the frontend cost of writing both is zero.
    ...(prefill.customerId
      ? { customer_id: prefill.customerId, customerId: prefill.customerId }
      : {}),
    customer_name: prefill.customerName,
    contact_id: prefill.contact?.id || null,
    salesperson_ids: prefill.salespersons.map((s) => s.id),
    // §4 (2026-07-29): persist full salesperson objects so they round-trip on reload.
    // The backend stores whatever we send; `salesperson_ids` alone loses the name.
    salespersons: prefill.salespersons,
    payment_term: prefill.paymentTerm,
    payment_type: prefill.paymentType,
    // Q-09 (§3ap, 2026-08-18): symmetric guard on the quote payload's own
    // billing_address stamp. Operator ruled BOTH call sites should be
    // guarded even though this one stamps the quote record (not the
    // customer). Rationale: a quote saved without a street should not
    // stamp six empty strings, and any downstream renderer that reads
    // quote.billing_address should fall through to the live customer
    // record via resolveQuoteDisplay.
    ...(hasStreet(prefill.billingAddress) ? { billing_address: prefill.billingAddress } : {}),
    ...((prefill.sameAsBilling
        ? hasStreet(prefill.billingAddress)
        : hasStreet(prefill.serviceAddress))
      ? { service_address: prefill.sameAsBilling ? prefill.billingAddress : prefill.serviceAddress }
      : {}),
    same_as_billing: prefill.sameAsBilling,
    // IDI-169: store the id so backend enrichment resolves the label on read
    leadSourceTypeId: prefill.leadSource || undefined,
    // IDI-169: project details — match existing migrated field names
    jobTypeId: prefill.jobTypeId || undefined,
    measurementMethod: prefill.measurementMethod || undefined,
    // IDI-178: `roundMaterials` (camelCase) matches the field name the
    // migration already writes on legacy-migrated quotes
    // (Quote_RoundMaterials -> roundMaterials, migration/cmd/migrate/entities.go).
    // Always emitted (not guarded by `|| undefined`) so an explicit "off"
    // is distinguishable from "never touched this quote" -- unlike most
    // other optional fields here, a false value is meaningful and must be
    // persisted, not omitted.
    roundMaterials: prefill.roundMaterials === true,
    line_items: serializedLineItems,
    // PR-4 §1b: builder-local state now reaches the payload.
    quote_options: Array.isArray(quoteOptions) ? quoteOptions : [],
    discounts: Array.isArray(discounts) ? discounts : [],
    notes: notes || '',
    validity_days: validityDays || 30,
    title: title || '',
    // DEF-151 (2026-08-04): emit authoritative subtotal from line items.
    // Mirrors buildViewQuote.js:104-161 derivation: SUM(line_total) over
    // non-accessory, non-option lines. The Go backend merge
    // (existing[k] = val) picks this up and overwrites the stale stored
    // value. Snapshot preserves the pre-fix value on first write only (S-4).
    subtotal: (() => {
      const OPTS = new Set(['options', 'optional', 'optional upgrades']);
      return serializedLineItems.reduce((sum, li) => {
        if (!li) return sum;
        if (li.categoryId === 'accessories' || li.category === 'accessories') return sum;
        const label = String(li.areaLabel || li.area_label || '').toLowerCase();
        if (OPTS.has(label) || li.is_option || li.type === 'option' || li.kind === 'option') return sum;
        const v = Number(li.line_total ?? li.lineTotal);
        return sum + (Number.isFinite(v) ? v : 0);
      }, 0);
    })(),
    // Snapshot: preserve the loaded quote's subtotal on first write only.
    // Guard: only emit when the loaded quote HAD a subtotal AND no snapshot
    // exists yet. The Go merge writes this field; subsequent saves see it
    // already present and skip.
    ...((_loadedSubtotal != null && _loadedSubtotalSnapshot == null)
      ? { subtotal_snapshot: _loadedSubtotal }
      : {}),
    ...cleanRestMetadata,
  };
}
